import React, { useMemo, useCallback, useContext } from "react";
import _ from "lodash";
import * as yup from "yup";
import Card from "../UI/Card";
import { Field } from "../UI/forms";
import { Flex } from "reflexbox";
import { Form, Formik } from "formik";
import ButtonCluster from "../UI/forms/ButtonCluster";
import AlertContext from "../../context/alert/AlertContext";
import {
  deleteRMADetail,
  updateRMADetail,
} from "../../requests/rmaDetailManagementReqs";

const RMADetailForm = ({
  productOptions,
  refetchDetails,
  rmaId,
  setRmaId,
  setShow,
  disabled,
  setDetailData,
  count,
  rows,
  isQuote,
}) => {
  const currentDetail = useMemo(() => {
    return rmaId === "create" ? null : _.find(rows, ({ id }) => id === rmaId);
  }, [rows, rmaId]);

  const initialValues = useMemo(() => {
    const {
      timeInService,
      reason,
      modelNo,
      quantity,
      orderedQuantity,
      productNumber,
    } = currentDetail || {};
    return {
      timeInService,
      reason: reason || "",
      modelNo,
      quantity,
      orderedQuantity,
      productNumber,
    };
  }, [currentDetail]);

  const detailValidation = useMemo(() => {
    const quantity = isQuote
      ? yup
          .number("Quantity must be a number.")
          .required("Quantity is required.")
          .min(1, "The minimum quantity to return is 1.")
          .max(
            currentDetail?.orderedQuantity > 1
              ? currentDetail?.orderedQuantity
              : 1,
            "You cannot return more items than were ordered."
          )
      : yup
          .number("Quantity must be a number.")
          .required("Quantity is required.")
          .min(1, "The minimum quantity to return is 1.");

    return yup.object().shape({
      productNumber: yup
        .string("You must select a product to return.")
        .required("You must select a product to return."),
      orderedQuantity: yup.number().nullable(),
      quantity,
      timeInService: yup.string().nullable(),
      reason: yup
        .string("You must provide a reason for the return.")
        .required("You must provide a reason for the return."),
      modelNo: yup
        .string()
        .required(
          "Please enter a model number, or NA if there is no Model Number"
        ),
    });
  }, [currentDetail, isQuote]);

  const { showFailureAlert, showSuccessAlert } = useContext(AlertContext);

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      resetForm({ values });
      try {
        const newRows = [...rows];
        const index = _.findIndex(rows, ({ id }) => id === rmaId);
        newRows.splice(index, 1, { ...currentDetail, ...values });

        setDetailData({ rows: newRows, count });
        setShow(false);

        await updateRMADetail({ id: rmaId, input: { ...values } });
        setRmaId(null);
      } catch (error) {
        console.error(error);
        showFailureAlert("Failed to update RMA Detail.");
        setRmaId(null);
        setShow(false);
      }
    },
    [
      count,
      currentDetail,
      rmaId,
      rows,
      setDetailData,
      setRmaId,
      setShow,
      showFailureAlert,
    ]
  );

  const onDetailDelete = useCallback(async () => {
    setShow(false);
    setRmaId(null);
    if (disabled) {
      showFailureAlert(
        "Cannot delete RMA Detail, RMA has already been submitted."
      );
      return;
    }

    try {
      const newRows = [...rows];
      _.remove(newRows, (row) => row.id === rmaId);
      setDetailData({ rows: newRows, count: count - 1 });
      await deleteRMADetail({ id: rmaId });
      showSuccessAlert(`Successfully deleted RMA Detail: ${rmaId}`);
    } catch (error) {
      console.error(error);
      showFailureAlert("Failed to delete RMA Detail.");
    }
  }, [
    disabled,
    showFailureAlert,
    rows,
    setDetailData,
    count,
    rmaId,
    showSuccessAlert,
    setShow,
    setRmaId,
  ]);

  return (
    <Card maxWidth="600px" minWidth={["300px", "320px", "400px", "600px"]}>
      <Formik
        validateOnChange={false}
        validateOnMount={false}
        validateOnBlur={false}
        onSubmit={onSubmit}
        validationSchema={detailValidation}
        initialValues={initialValues}
      >
        {({ values }) => {
          return (
            <Form style={{ width: "100%" }}>
              <Flex
                width={1}
                alignItems="flex-start"
                justifyContent="flex-start"
                flexDirection="column"
              >
                <Field
                  options={productOptions}
                  name="productNumber"
                  required
                  typeableSelect
                  disabled={disabled}
                  labelText="Product Number"
                />

                {isQuote ? (
                  <Flex
                    alignItems="flex-start"
                    justifyContent="space-between"
                    style={{ gap: "1rem" }}
                  >
                    <Field
                      disabled={true}
                      labelText="Ordered Quantity"
                      name="orderedQuantity"
                      type="number"
                    />

                    <Field
                      disabled={disabled}
                      required
                      max={values.orderedQuantity}
                      labelText="Quantity to Return"
                      name="quantity"
                      type="number"
                    />
                  </Flex>
                ) : (
                  <Field
                    required
                    disabled={disabled}
                    labelText="Quantity to Return"
                    name="quantity"
                    type="number"
                  />
                )}

                <Field
                  disabled={disabled}
                  labelText="Reason for RMA"
                  as="textarea"
                  name="reason"
                  required
                  fullHeight
                />
                <Field
                  required
                  disabled={disabled}
                  labelText="Model Number"
                  name="modelNo"
                />

                <ButtonCluster
                  onDelete={onDetailDelete}
                  hideDelete={rmaId === "create" || disabled}
                  recordAction={rmaId === "create" ? "create" : "edit"}
                  onCancel={() => setShow(false)}
                  deleteMessage="Are you sure you want to delete this Detail line?"
                />
              </Flex>
            </Form>
          );
        }}
      </Formik>
    </Card>
  );
};

export default RMADetailForm;
